import { useState, useEffect } from 'react';
import axios from 'axios';
import { type User } from './types';
import { Search, DollarSign, TrendingUp, Users } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

interface SubscriptionStats {
  total: number; free: number; standard: number; premium: number;
  totalRevenue: number; averageRevenue: number;
}

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm";
const INPUT = "bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary/50 transition-all";

function SubscriptionsPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<SubscriptionStats>({ total: 0, free: 0, standard: 0, premium: 0, totalRevenue: 0, averageRevenue: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState('');

  useEffect(() => { fetchSubscriptions(); }, [searchTerm, tierFilter]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: searchTerm, tier: tierFilter, limit: 100 }
      });
      const users = response.data.users;
      setUsers(users);
      const free = users.filter((u: User) => u.tier === 'free').length;
      const standard = users.filter((u: User) => u.tier === 'standard').length;
      const premium = users.filter((u: User) => u.tier === 'premium').length;
      const totalRevenue = (standard * 6.99) + (premium * 9.99);
      setStats({ total: users.length, free, standard, premium, totalRevenue, averageRevenue: users.length > 0 ? totalRevenue / users.length : 0 });
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'premium': return 'bg-purple/25 text-purple border border-purple/40';
      case 'standard': return 'bg-primary/25 text-primary border border-primary/40';
      default: return 'bg-white/[0.08] text-white/70 border border-white/20';
    }
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const summaryCards = [
    { icon: <Users size={18} />, label: 'Total Subscribers', value: stats.total, sub: 'All tiers combined', color: '#007AFF' },
    { icon: <DollarSign size={18} />, label: 'Monthly Revenue', value: `$${stats.totalRevenue.toFixed(2)}`, sub: 'MRR from paid tiers', color: '#34C759' },
    { icon: <TrendingUp size={18} />, label: 'Conversion Rate', value: `${(((stats.standard + stats.premium) / stats.total * 100) || 0).toFixed(1)}%`, sub: 'Free to paid', color: '#FF9F0A' },
  ];

  const tierCards = [
    { label: 'Free Tier', price: '$0.00/month', count: stats.free, revenue: '$0.00', color: 'rgba(255,255,255,0.2)' },
    { label: 'Standard Tier', price: '$6.99/month', count: stats.standard, revenue: `$${(stats.standard * 6.99).toFixed(2)}`, color: '#007AFF' },
    { label: 'Premium Tier', price: '$9.99/month', count: stats.premium, revenue: `$${(stats.premium * 9.99).toFixed(2)}`, color: '#BF5AF2' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Subscription Management</h1>
        <p className="text-white/40 text-sm mt-1">Monitor and manage user subscriptions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {summaryCards.map((c, i) => (
          <div key={i} className={`${CARD} p-5 flex items-start gap-3`}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${c.color}20`, color: c.color }}>
              {c.icon}
            </div>
            <div>
              <p className="text-white/40 text-xs mb-0.5">{c.label}</p>
              <p className="text-2xl font-bold text-white">{c.value}</p>
              <p className="text-white/30 text-xs mt-0.5">{c.sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tier Breakdown */}
      <div className={`${CARD} p-5`}>
        <h2 className="text-sm font-semibold text-white mb-4">Subscription Tier Breakdown</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {tierCards.map((t, i) => (
            <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4" style={{ borderLeftColor: t.color, borderLeftWidth: 3 }}>
              <p className="text-sm font-semibold text-white/80">{t.label}</p>
              <p className="text-white/40 text-xs mt-0.5">{t.price}</p>
              <p className="text-2xl font-bold text-white mt-2">{t.count}</p>
              <p className="text-white/30 text-xs">{((t.count / stats.total) * 100 || 0).toFixed(1)}% of users</p>
              <p className="text-white/40 text-xs mt-1">Revenue: {t.revenue}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input type="text" placeholder="Search by email..." className={`${INPUT} pl-8 w-56`}
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <select className={INPUT} value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
          <option value="">All Tiers</option>
          <option value="free">Free</option>
          <option value="standard">Standard</option>
          <option value="premium">Premium</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-2 border-white/10 border-t-primary rounded-full animate-spin" />
        </div>
      ) : (
        <div className={CARD}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {['User', 'Email', 'Tier', 'Joined', 'Monthly Value', 'Status'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/30 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">
                          {user.email ? user.email.charAt(0).toUpperCase() : '?'}
                        </div>
                        <span className="text-white/80 text-xs">{user.name || user.email.split('@')[0]}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/50 text-xs">{user.email}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getTierBadge(user.tier)}`}>
                        {(user.tier || '').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/50 text-xs">{formatDate(user.createdAt)}</td>
                    <td className="px-4 py-3 text-white/70 text-xs font-medium">
                      {user.tier === 'free' && '$0.00'}
                      {user.tier === 'standard' && '$6.99'}
                      {user.tier === 'premium' && '$9.99'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${user.tier === 'free' ? 'bg-white/[0.08] text-white/70 border border-white/20' : 'bg-green/20 text-green border border-green/30'}`}>
                        {user.tier === 'free' ? 'Free User' : 'Paying'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionsPage;
